import { useEffect, useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import type { TenantFeatures } from '../../lib/superAdmin';
import {
  currentEmail,
  signIn,
  signOut,
  requestPasswordReset,
  updatePassword,
  validateNewPassword,
  onPasswordRecovery,
} from '../../lib/authService';
import AdminShell from './AdminShell';
import PasswordInput from '../PasswordInput';

/**
 * Garde d'accès au back-office : exige une session Supabase connectée.
 * Sans session → écran de connexion (brandé tenant). Avec session → AdminShell + déconnexion.
 * Gère aussi le **mot de passe oublié** : lien sur la connexion (email de réinitialisation)
 * + écran « nouveau mot de passe » quand le gérant revient via le lien (mode recovery).
 *
 * ⚠️ Barrière de fond = RLS serveur (autorisation par tenant). Ce garde côté client
 * empêche l'accès anonyme et porte l'UX ; il ne remplace pas les politiques RLS.
 */
export default function AdminGuard({ tenant, features = {} }: { tenant: Tenant; features?: TenantFeatures }) {
  const [email, setEmail] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [recovery, setRecovery] = useState(false);

  useEffect(() => {
    // Mode « réinitialisation » : détecté via le hash du lien email ET via l'event Supabase.
    if ((window.location.hash || '').includes('type=recovery')) setRecovery(true);
    const unsub = onPasswordRecovery(() => setRecovery(true));
    currentEmail().then((e) => {
      setEmail(e);
      setChecking(false);
    });
    return unsub;
  }, []);

  if (recovery) {
    return <RecoveryForm tenant={tenant} />;
  }

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="opacity-70">Vérification de l'accès…</p>
      </div>
    );
  }

  if (!email) {
    return <AdminLogin tenant={tenant} onAuthed={(e) => setEmail(e)} />;
  }

  return (
    <AdminShell
      tenant={tenant}
      features={features}
      adminEmail={email}
      onSignOut={async () => {
        await signOut();
        setEmail(null);
      }}
    />
  );
}

/** Carte brandée (logo + nom du resto) réutilisée par les écrans d'auth. */
function BrandHeader({ tenant, subtitle }: { tenant: Tenant; subtitle: string }) {
  const brand = tenant.primary_color || tenant.brand_color || '#E65100';
  return (
    <div className="mb-5 flex items-center gap-3">
      {tenant.logo_url ? (
        <img src={tenant.logo_url} alt="" className="h-11 w-11 rounded-lg object-contain" />
      ) : (
        <span
          className="flex h-11 w-11 items-center justify-center rounded-lg text-xl font-black text-white"
          style={{ background: brand }}
        >
          {tenant.name.slice(0, 1)}
        </span>
      )}
      <div>
        <div className="font-extrabold">{tenant.name}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
      </div>
    </div>
  );
}

function AdminLogin({ tenant, onAuthed }: { tenant: Tenant; onAuthed: (email: string) => void }) {
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const brand = tenant.primary_color || tenant.brand_color || '#E65100';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await signIn(email, password);
    if (!res.ok) {
      setBusy(false);
      setError(res.error ?? 'Connexion impossible.');
      return;
    }
    const who = await currentEmail();
    setBusy(false);
    if (who) onAuthed(who);
    else setError('Session non établie. Réessayez.');
  };

  const sendReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    // Le lien email ramène le gérant sur l'admin de SON resto.
    const redirectTo = `${window.location.origin}/r/${tenant.slug}/admin`;
    const res = await requestPasswordReset(email, redirectTo);
    setBusy(false);
    if (!res.ok) {
      setError(res.error ?? 'Envoi impossible.');
      return;
    }
    setInfo('Si un compte existe pour cet email, un lien de réinitialisation vient d’être envoyé. Vérifiez votre boîte mail (et les spams).');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
        <BrandHeader tenant={tenant} subtitle={mode === 'login' ? 'Espace gérant — connexion' : 'Réinitialiser le mot de passe'} />

        {mode === 'login' ? (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <label className="text-sm font-semibold">
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="text-sm font-semibold">
              Mot de passe
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="rounded-lg px-4 py-2.5 font-bold text-white disabled:opacity-40"
              style={{ background: brand }}
            >
              {busy ? 'Connexion…' : 'Se connecter'}
            </button>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="button"
              onClick={() => {
                setMode('forgot');
                setError(null);
                setInfo(null);
              }}
              className="mt-1 text-center text-sm font-semibold text-slate-500 hover:underline"
            >
              Mot de passe oublié ?
            </button>
          </form>
        ) : (
          <form onSubmit={sendReset} className="flex flex-col gap-3">
            <p className="text-sm text-slate-600">
              Entrez l’email de votre compte gérant : on vous envoie un lien pour choisir un nouveau mot de passe.
            </p>
            <label className="text-sm font-semibold">
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="rounded-lg px-4 py-2.5 font-bold text-white disabled:opacity-40"
              style={{ background: brand }}
            >
              {busy ? 'Envoi…' : 'Envoyer le lien'}
            </button>
            {info && <p className="text-sm text-emerald-700">{info}</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="button"
              onClick={() => {
                setMode('login');
                setError(null);
                setInfo(null);
              }}
              className="mt-1 text-center text-sm font-semibold text-slate-500 hover:underline"
            >
              ← Retour à la connexion
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/** Écran « nouveau mot de passe » — affiché quand le gérant arrive via le lien email. */
function RecoveryForm({ tenant }: { tenant: Tenant }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const brand = tenant.primary_color || tenant.brand_color || '#E65100';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const invalid = validateNewPassword(password, confirm);
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError(null);
    const res = await updatePassword(password);
    setBusy(false);
    if (!res.ok) {
      setError(res.error ?? 'Échec de la mise à jour.');
      return;
    }
    setDone(true);
    await signOut();
  };

  const goToLogin = () => {
    // Nettoie le token de l'URL et revient sur la connexion propre.
    window.location.href = `${window.location.origin}/r/${tenant.slug}/admin`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
        <BrandHeader tenant={tenant} subtitle="Nouveau mot de passe" />
        {done ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-emerald-700">✅ Mot de passe mis à jour. Vous pouvez vous reconnecter.</p>
            <button
              onClick={goToLogin}
              className="rounded-lg px-4 py-2.5 font-bold text-white"
              style={{ background: brand }}
            >
              Aller à la connexion
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <label className="text-sm font-semibold">
              Nouveau mot de passe
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label className="text-sm font-semibold">
              Confirmer le mot de passe
              <PasswordInput
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="rounded-lg px-4 py-2.5 font-bold text-white disabled:opacity-40"
              style={{ background: brand }}
            >
              {busy ? 'Mise à jour…' : 'Valider le nouveau mot de passe'}
            </button>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
